import React, { useEffect } from 'react'
import moment, { type Moment } from 'moment'

import TimeframeButton from './TimeframeButton'
import {
  type CalloutType,
  type CustomAggregationsProps,
  type GetAggregationProps,
  type TimeframeProps,
} from '../TimeframesType'
import { getNextAvailableTimeframeOption } from '../../../services/TimeframeService'
import styles from '../_timeframe-filter.module.scss'
import { c } from '../../../translations/LibraryTranslationService'

type PreviousTimeframeProps = {
  /** Function to be called when an option is clicked */
  callout: (
    key1: CalloutType,
    key2: Moment,
    key3: Moment,
    key4: TimeframeProps,
  ) => void
  /** Function to be called to close popover */
  closeMenu?: () => void
  /** Selected time frame */
  timeframe: TimeframeProps
  /** Show/Hide aggregations based on this boolean value */
  useTimeframeAggregations: boolean
  /** Custom aggregations */
  customAggregations: CustomAggregationsProps
  /** Disabled timeframe options */
  disabledTimeframeOptions: string[]
}

const timeframes = [
  {
    id: 1,
    display: c('day'),
    timeValue: 'day',
    type: 'previous',
  },
  {
    id: 2,
    display: c('week'),
    timeValue: 'week',
    type: 'previous',
  },
  {
    id: 3,
    display: c('month'),
    timeValue: 'month',
    type: 'previous',
  },
  {
    id: 4,
    display: c('qtr'),
    timeValue: 'quarter',
    type: 'previous',
  },
  {
    id: 5,
    display: c('year'),
    timeValue: 'year',
    type: 'previous',
  },
]

export const PreviousTimeframe = ({
  callout,
  closeMenu,
  timeframe,
  useTimeframeAggregations = false,
  customAggregations,
  disabledTimeframeOptions,
}: PreviousTimeframeProps): React.JSX.Element => {
  useEffect(() => {
    // If currently selected timeframe option is disabled for visited page
    // then switch to next available timeframe option.
    if (disabledTimeframeOptions?.includes(timeframe.display)) {
      getNextAvailableTimeframeOption({
        timeframes,
        type: timeframe.type,
        disabledTimeframeOptions,
        callout,
        useTimeframeAggregations,
        customAggregations,
        getAggregation,
      })
    }
  }, [
    disabledTimeframeOptions,
    callout,
    customAggregations,
    timeframe.display,
    useTimeframeAggregations,
    timeframe.type,
  ])

  return (
    <div className={styles.timeframeFilter}>
      {timeframes.map((t: TimeframeProps) => {
        return (
          <TimeframeButton
            el={t}
            key={t.id}
            disabled={
              (disabledTimeframeOptions?.includes(t.display) || t.disabled) ??
              false
            }
            updateTimeframe={() => {
              callout(
                'UPDATE_RANGE',
                moment()
                  .subtract(1, t.timeValue as keyof TimeframeProps['timeValue'])
                  .startOf(t.timeValue as keyof TimeframeProps['timeValue']),
                moment()
                  .subtract(1, t.timeValue as keyof TimeframeProps['timeValue'])
                  .endOf(t.timeValue as keyof TimeframeProps['timeValue']),
                {
                  type: 'previous',
                  display: t.display,
                  timeValue: t.timeValue as string,
                  ...(useTimeframeAggregations
                    ? {
                        aggregation: getAggregation({
                          timeValue: t.timeValue as string,
                          customAggregations,
                        }),
                      }
                    : {}),
                },
              )
              closeMenu?.()
            }}
            timeframe={timeframe}
          />
        )
      })}
    </div>
  )
}

function getAggregation({
  timeValue,
  customAggregations,
}: GetAggregationProps) {
  const validAggregations = getValidAggregationsForPreviousTimeframe(
    timeValue,
    customAggregations,
  )
  return validAggregations?.[0]
}

export function getValidAggregationsForPreviousTimeframe(
  timeValue: string,
  customAggregations: CustomAggregationsProps,
) {
  // NOTE: The first item in the array will be the default for each timeValue
  switch (timeValue) {
    case 'day':
      return customAggregations ? customAggregations.day : ['hour']
    case 'week':
      return customAggregations ? customAggregations.week : ['day']
    case 'month':
      return customAggregations ? customAggregations.month : ['day', 'week']
    case 'quarter':
      return customAggregations
        ? customAggregations.quarter
        : ['month', 'week', 'day']
    case 'year':
      return customAggregations
        ? customAggregations.year
        : ['month', 'qtr', 'week', 'day']
    default:
      throw new Error(c('errorInvalidDisplayValue'))
  }
}
